import React, { useEffect, useState, useCallback } from "react";
import { Boxes, Heart, Share2, Download, Trash2, Loader2, Star, GitFork } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

// Community creative-share for sandbox (voxel) games: share your build & load others'.
export default function SandboxShare({ canvasRef, gameId, user, autoRemixBuildId }) {
  const [builds, setBuilds] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [remixSource, setRemixSource] = useState(null);

  const loadBuilds = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sandbox/builds`, { params: { game_id: gameId, sort } });
      setBuilds(r.data?.builds || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [gameId, sort]);

  useEffect(() => { loadBuilds(); }, [loadBuilds]);
  useEffect(() => { api.get("/sandbox/featured").then((r) => setFeatured(r.data?.build || null)).catch(() => {}); }, []);

  // Remember which community build was loaded (incl. via the ?build spotlight auto-load)
  // so re-sharing credits the original creator.
  useEffect(() => {
    if (!autoRemixBuildId || remixSource) return;
    const found = builds.find((b) => b.id === autoRemixBuildId) || (featured && featured.id === autoRemixBuildId ? featured : null);
    if (found) { setRemixSource({ id: found.id, name: found.name }); return; }
    api.get(`/sandbox/builds/${autoRemixBuildId}`).then((r) => {
      if (r.data?.id) setRemixSource({ id: r.data.id, name: r.data.name });
    }).catch(() => {});
  }, [autoRemixBuildId, builds, featured, remixSource]);

  const submitShare = async () => {
    if (!name.trim()) { toast.error("Give your creation a name!"); return; }
    setBusy(true);
    try {
      const blocks = await canvasRef.current?.exportSandbox?.();
      if (!blocks || blocks.length < 1) {
        toast.error("Build at least one block in the game above before sharing!");
        setBusy(false);
        return;
      }
      await api.post(`/sandbox/builds`, { game_id: gameId, name: name.trim(), blocks, remixed_from: remixSource?.id || null });
      toast.success(remixSource ? `🔁 Remix of "${remixSource.name}" shared — original creator credited!` : "🎉 Creation shared to the community!");
      setOpen(false); setName("");
      setSort("recent");
      await loadBuilds();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't share your build");
    }
    setBusy(false);
  };

  const loadBuild = async (b) => {
    try {
      const r = await api.get(`/sandbox/builds/${b.id}`);
      const blocks = r.data?.blocks || [];
      canvasRef.current?.loadSandbox?.(blocks);
      setRemixSource({ id: b.id, name: b.name });
      toast.success(`Loaded "${b.name}" — add your own blocks & re-share to remix it!`);
      setBuilds((prev) => prev.map((x) => x.id === b.id ? { ...x, loads: (x.loads || 0) + 1 } : x));
    } catch {
      toast.error("Couldn't load that build");
    }
  };

  const toggleLike = async (b) => {
    try {
      const r = await api.post(`/sandbox/builds/${b.id}/like`);
      setBuilds((prev) => prev.map((x) => x.id === b.id ? { ...x, liked: r.data.liked, likes: r.data.likes } : x));
    } catch { /* ignore */ }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete "${b.name}"?`)) return;
    try {
      await api.delete(`/sandbox/builds/${b.id}`);
      setBuilds((prev) => prev.filter((x) => x.id !== b.id));
      toast.success("Deleted");
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const maxRemix = Math.max(0, ...builds.map((b) => b.remix_count || 0));

  return (
    <div className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="sandbox-share">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h3 className="font-display font-bold flex items-center gap-2">
          <Boxes className="w-5 h-5 text-[hsl(var(--primary))]" /> Community creations
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-[hsl(var(--border))] overflow-hidden text-xs">
            {["recent", "popular", "played", "remixed"].map((s) => (
              <button key={s} onClick={() => setSort(s)} data-testid={`sandbox-sort-${s}`}
                className={`px-3 py-1.5 font-semibold capitalize transition-colors ${sort === s ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-[hsl(var(--secondary))]/50"}`}>
                {s}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={() => setOpen(true)} className="rounded-full" data-testid="sandbox-share-btn">
            <Share2 className="w-4 h-4" /> Share your build
          </Button>
        </div>
      </div>

      {featured && (
        <div className="mb-4 rounded-2xl p-4 bg-gradient-to-br from-amber-500/10 to-[hsl(var(--primary))]/10 border border-amber-500/30" data-testid="sandbox-featured">
          <div className="flex items-center gap-1.5 mb-2 text-sm font-bold text-amber-500"><Star className="w-4 h-4 fill-amber-500" /> Creation of the week</div>
          <div className="flex items-center gap-3">
            <div className="text-3xl" aria-hidden>{featured.owner_avatar || "🧱"}</div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold truncate">{featured.name}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">by {featured.owner_name} · {featured.block_count} blocks · ❤️ {featured.likes || 0} · ▶ {featured.loads || 0}{featured.remix_count > 0 ? ` · 🔁 ${featured.remix_count}` : ""}</div>
            </div>
            <Button size="sm" className="rounded-full" onClick={() => loadBuild(featured)} data-testid="sandbox-featured-load">
              <Download className="w-3.5 h-3.5" /> Load
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--muted-foreground))]" /></div>
      ) : builds.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))] py-3">No creations shared yet — build something in the game above and be the first! 🧱</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="sandbox-build-list">
          {builds.map((b) => (
            <div key={b.id} className="rounded-xl border border-[hsl(var(--border))] p-3 flex flex-col gap-2" data-testid={`sandbox-build-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold flex items-center gap-1.5 min-w-0">
                    <span className="truncate">{b.name}</span>
                    {maxRemix > 0 && b.remix_count === maxRemix && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-500 shrink-0 whitespace-nowrap" data-testid={`sandbox-top-remix-${b.id}`}>👑 Most remixed</span>
                    )}
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{b.owner_avatar} {b.owner_name} · {b.block_count} blocks</div>
                  {b.remixed_from_name && (
                    <div className="text-[11px] text-[hsl(var(--primary))] flex items-center gap-1 mt-0.5 truncate" data-testid={`sandbox-remix-from-${b.id}`}>
                      <GitFork className="w-3 h-3 shrink-0" /> Remixed from {b.remixed_from_name}{b.remixed_from_owner ? ` · ${b.remixed_from_owner}` : ""}
                    </div>
                  )}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] whitespace-nowrap text-right">
                  <div>▶ {b.loads || 0}</div>
                  {b.remix_count > 0 && <div className="text-[hsl(var(--primary))]" data-testid={`sandbox-remix-count-${b.id}`}>🔁 {b.remix_count}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" className="rounded-full flex-1" onClick={() => loadBuild(b)} data-testid={`sandbox-load-${b.id}`}>
                  <Download className="w-3.5 h-3.5" /> Load
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => toggleLike(b)} data-testid={`sandbox-like-${b.id}`}>
                  <Heart className={`w-3.5 h-3.5 ${b.liked ? "fill-rose-500 text-rose-500" : ""}`} /> {b.likes || 0}
                </Button>
                {b.is_mine && (
                  <Button size="sm" variant="ghost" className="rounded-full text-rose-500" onClick={() => del(b)} data-testid={`sandbox-delete-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="sandbox-share-dialog">
          <DialogHeader>
            <DialogTitle>Share your creation 🧱</DialogTitle>
            <DialogDescription className="sr-only">Share your voxel creation to the community gallery for others to load and remix.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">We'll snapshot the blocks you've placed in the game above and add them to the community gallery for others to load and build on.</p>
          {remixSource && (
            <div className="text-xs rounded-lg bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 px-3 py-2 flex items-center gap-1.5" data-testid="sandbox-remix-credit">
              <GitFork className="w-3.5 h-3.5 text-[hsl(var(--primary))] shrink-0" /> Remixing <b>"{remixSource.name}"</b> — the original creator will be credited.
            </div>
          )}
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
            placeholder="e.g. Rainbow castle, My spaceship…" data-testid="sandbox-name-input"
            onKeyDown={(e) => { if (e.key === "Enter") submitShare(); }} />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} data-testid="sandbox-cancel-btn">Cancel</Button>
            <Button className="rounded-full" onClick={submitShare} disabled={busy} data-testid="sandbox-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
